import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../shared/services/auth.service';
import { CollaborationAction, CollaborationItem } from '../../models/collaboration.model';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { ApiService } from '../../../shared/services/api.service';
import { GetCollaboration, GetCollaborationList } from '../../../../graphql/queries';
import {
  CollaborationListRequest,
  GetCollaborationListQuery,
  GetCollaborationListQueryVariables,
  Collaboration,
  Action,
  CollaborationStatus,
  OrgType,
  AcceptOrDeclineCollaborationMutation,
  AcceptOrDeclineCollaborationMutationVariables,
  DeleteCollaborationMutation,
  DeleteCollaborationMutationVariables,
  UpdateCollaborationMutation,
  UpdateCollaborationMutationVariables,
  SendCollaborationInviteMutation,
  SendCollaborationInviteMutationVariables,
  GetCollaborationQuery,
  GetCollaborationQueryVariables,
} from '../../../../API';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CollaborationDataService } from '../../services/collaboration-data.service';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import {
  AcceptOrDeclineCollaboration,
  DeleteCollaboration,
  SendCollaborationInvite,
  UpdateCollaboration,
} from '../../../../graphql/mutations';
import { AppService } from '../../../shared/services/app.service';

@Component({
  selector: 'app-collaboration-list',
  templateUrl: './collaboration-list.component.html',
  styleUrl: './collaboration-list.component.scss',
})
export class CollaborationListComponent implements OnInit, OnDestroy {
  collaborationData: CollaborationItem[] = [];
  isLoading = false;
  searchValue = '';
  filteredData: CollaborationItem[] = [];
  engagement: string = '';
  headers: any[] = [];
  totalCount?: number;
  lastModifiedByFilter: { text: string; value: string }[] = [];
  statusFilter: { text: string; value: string }[] = [
    { text: 'Active', value: CollaborationStatus.ACTIVE },
    { text: 'Pending', value: CollaborationStatus.PENDING },
    { text: 'Draft', value: CollaborationStatus.DRAFT },
    { text: 'On Hold', value: CollaborationStatus.ON_HOLD },
    { text: 'Declined', value: CollaborationStatus.INVITATION_DECLINED },
    { text: 'Closed', value: CollaborationStatus.CLOSED },
  ];

  @ViewChild('confirmDeclineModal') confirmDeclineModal!: TemplateRef<any>;
  @ViewChild('confirmDeleteModal') confirmDeleteModal!: TemplateRef<any>;
  @ViewChild('confirmPauseActionModal') confirmPauseActionModal!: TemplateRef<any>;
  @ViewChild('confirmCloseActionModal') confirmCloseActionModal!: TemplateRef<any>;

  declineModalRef!: NzModalRef;
  closeModalRef!: NzModalRef;
  pauseModalRef!: NzModalRef;
  deleteModalRef!: NzModalRef;
  selectedCollaboration: CollaborationItem | null = null;

  private subscription: Subscription = new Subscription();

  canViewCreateCollaboration: boolean = false;
  isSponsorOrg: boolean = false;
  CollaborationAction = CollaborationAction;
  isViewDatasetsModalVisible: boolean = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public authService: AuthService,
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private messageService: NzMessageService,
    private collaborationDataService: CollaborationDataService,
    private nzModalService: NzModalService,
    private nzMessageService: NzMessageService,
    private appService: AppService,
  ) {}

  ngOnInit(): void {
    const loggedInUserOrg = this.userManagementService.getOrganizationType();
    if (loggedInUserOrg == OrgType.CLUB || loggedInUserOrg == OrgType.LEAGUE) {
      this.canViewCreateCollaboration = true;
    }
    this.isSponsorOrg = loggedInUserOrg === OrgType.SPONSOR;

    this.loadCollaborationData();
    this.setHeaders();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async loadCollaborationData(): Promise<void> {
    this.isLoading = true;
    const loggedInUserOrg = this.userManagementService.getOrganizationType();
    const userOrgType = this.userManagementService.getOrgType();

    const collaborationRequest: CollaborationListRequest = {
      action: Action.LIST,
      loggedInUserOrg: loggedInUserOrg as OrgType,
      orgType: userOrgType,
    };

    try {
      const response = await this.apiService.query<GetCollaborationListQuery, GetCollaborationListQueryVariables>(
        GetCollaborationList,
        { collaborationRequest },
      );

      const result = response.data?.GetCollaborationList;
      if (result?.success && result.collaborationList) {
        this.collaborationData = result.collaborationList.map((collaboration: Collaboration) => ({
          collaborationId: collaboration.collaborationId,
          name: collaboration.name,
          description: collaboration.description,
          objective: collaboration.objective,
          status: collaboration.status,
          lastModifiedAt: collaboration.lastModifiedAt || '',
          createdBy: collaboration.createdBy || '',
          createdAt: collaboration.createdAt || '',
          lastModifiedBy: collaboration.lastModifiedBy || '',
          orgType: collaboration.orgType,
          ownerDatasets: collaboration.ownerDatasets || [],
          partnerDatasets: collaboration.partnerDatasets || [],
          partnerEmail: collaboration.partnerEmail || [],
          partnerInviteMessage: collaboration.partnerInviteMessage || '',
          partnerOrgType: collaboration.partnerOrgType || '',
        }));
        this.filteredData = [...this.collaborationData];
        this.setLastModifiedByFilter();
        this.getPageData();
      } else {
        this.messageService.error(result?.message || 'Failed to load collaborations');
      }
    } catch (error: any) {
      console.error('Error loading collaborations:', error);
      this.messageService.error('Failed to load collaborations. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Collaboration Entities',
        width: '13rem',
      },
      {
        name: 'Name',
        sortOrder: null,
        sortFn: (a: CollaborationItem, b: CollaborationItem) => (a.name || '').localeCompare(b.name || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '10rem',
      },
      {
        name: 'Description',
        sortOrder: null,
        sortFn: (a: CollaborationItem, b: CollaborationItem) =>
          (a.description || '').localeCompare(b.description || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '15rem',
      },
      {
        name: 'Objective',
        sortOrder: null,
        sortFn: (a: CollaborationItem, b: CollaborationItem) => (a.objective || '').localeCompare(b.objective || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '15rem',
      },
      {
        name: 'Status',
        listOfFilter: this.statusFilter,
        sortOrder: null,
        filterFn: this.statusFilterFn,
        filterMultiple: true,
        sortFn: (a: CollaborationItem, b: CollaborationItem) => (a.status || '').localeCompare(b.status || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '6rem',
      },
      {
        name: 'Datasets',
        width: '8rem',
      },
      {
        name: 'Last Updated',
        sortOrder: 'descend',
        sortFn: (a: CollaborationItem, b: CollaborationItem) => {
          const dateA = new Date(a.lastModifiedAt).getTime();
          const dateB = new Date(b.lastModifiedAt).getTime();
          return dateA - dateB;
        },
        sortDirections: ['ascend', 'descend', null],
        width: '15rem',
      },
      {
        name: 'Owners',
        sortOrder: null,
        listOfFilter: this.lastModifiedByFilter,
        filterFn: this.ownerFilterFn,
        sortFn: (a: CollaborationItem, b: CollaborationItem) => (a.createdBy || '').localeCompare(b.createdBy || ''),
        sortDirections: ['ascend', 'descend', null],
        width: '12rem',
        filterMultiple: true,
        key: 'owner',
      },
      {
        name: 'Actions',
        width: '6rem',
        right: true,
      },
    ];
  }

  ownerFilterFn = (ownerList: string[], collaboration: CollaborationItem): boolean => {
    return ownerList.some((owner) => (collaboration.createdBy || '--') === owner);
  };

  statusFilterFn = (statusList: string[], collaboration: CollaborationItem): boolean => {
    return statusList.some((status) => collaboration.status === status);
  };

  setLastModifiedByFilter(): void {
    this.lastModifiedByFilter = this.collaborationData
      .map((collaboration) => collaboration.createdBy)
      .filter((createdBy, index, arr) => createdBy && arr.indexOf(createdBy) === index)
      .map((createdBy) => {
        return {
          text: createdBy || '--',
          value: createdBy || '--',
        };
      });

    const ownerHeader = this.headers.find((head) => head.key === 'owner');
    if (ownerHeader) {
      ownerHeader.listOfFilter = this.lastModifiedByFilter;
    }
  }

  onSearch(): void {
    if (!this.searchValue.trim()) {
      this.filteredData = [...this.collaborationData];
    } else {
      const searchTerm = this.searchValue.toLowerCase();
      this.filteredData = this.collaborationData.filter(
        (item: CollaborationItem) =>
          (item.name || '').toLowerCase().includes(searchTerm) ||
          (item.description || '').toLowerCase().includes(searchTerm) ||
          (item.createdBy || '').toLowerCase().includes(searchTerm) ||
          (item.status || '').toLowerCase().includes(searchTerm) ||
          (item.orgType || '').toLowerCase().includes(searchTerm) ||
          (item.partnerOrgType || '').toLowerCase().includes(searchTerm),
      );
    }
    this.getPageData();
  }

  onCreateCollaboration(): void {
    this.router.navigate(['../create'], { relativeTo: this.activatedRoute });
  }

  onCollaborationAction(action: CollaborationAction, collaboration: CollaborationItem): void {
    switch (action) {
      case CollaborationAction.EDIT:
        this.router.navigate(['../edit', collaboration.collaborationId], { relativeTo: this.activatedRoute });
        break;
      case CollaborationAction.DELETE:
        this.deleteCollaboration(collaboration, this.confirmDeleteModal);
        break;
      case CollaborationAction.MANAGE_DATASETS:
        this.onManageDatasets(collaboration);
        break;
      case CollaborationAction.PAUSE:
        this.pauseCollaboration(collaboration, this.confirmPauseActionModal);
        break;
      case CollaborationAction.CLOSE:
        this.closeCollaboration(collaboration, this.confirmCloseActionModal);
        break;
      case CollaborationAction.RESUME:
        this.handleAction(collaboration, Action.RESUME, CollaborationStatus.ACTIVE, 'resume');
        break;
      case CollaborationAction.ACCEPT:
        this.router.navigate(['/collaboration/view-invitation', collaboration.collaborationId]);
        break;
      case CollaborationAction.DECLINE:
        this.declineInvite(collaboration, this.confirmDeclineModal);
        break;
      case CollaborationAction.RESEND:
        this.getCollaboration(collaboration);
        break;
      default:
        break;
    }
  }

  getPageData(): void {
    this.totalCount = this.filteredData?.length;
  }

  getStatusColor(status: CollaborationStatus): string {
    switch (status) {
      case CollaborationStatus.ACTIVE:
        return 'success';
      case CollaborationStatus.DRAFT:
      case CollaborationStatus.ON_HOLD:
      case CollaborationStatus.PENDING:
        return 'warning';
      case CollaborationStatus.CLOSED:
      case CollaborationStatus.INVITATION_DECLINED:
        return 'error';
      default:
        return 'default';
    }
  }

  getDisplayStatus(status: CollaborationStatus): string {
    switch (status) {
      case CollaborationStatus.ACTIVE:
        return 'Active';
      case CollaborationStatus.DRAFT:
        return 'Draft';
      case CollaborationStatus.ON_HOLD:
        return 'On Hold';
      case CollaborationStatus.PENDING:
        return 'Pending';
      case CollaborationStatus.CLOSED:
        return 'Closed';
      case CollaborationStatus.INVITATION_DECLINED:
        return 'Declined';
      default:
        return 'default';
    }
  }

  getCollaborationEntity(collaboration: CollaborationItem): string {
    if (collaboration.status === CollaborationStatus.DRAFT) return collaboration.orgType;
    return `${collaboration.orgType} x ${collaboration.partnerOrgType}`;
  }

  canEditOrDelete(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) return false;
    return (
      collaboration.status === CollaborationStatus.DRAFT ||
      collaboration.status === CollaborationStatus.INVITATION_DECLINED
    );
  }

  canAddRemoveDatasets(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) {
      return collaboration.status === CollaborationStatus.ACTIVE;
    }
    return collaboration.status === CollaborationStatus.PENDING || collaboration.status === CollaborationStatus.ACTIVE;
  }

  canPause(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) return false;
    return collaboration.status === CollaborationStatus.ACTIVE;
  }

  canClose(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) return false;
    return collaboration.status === CollaborationStatus.ACTIVE || collaboration.status === CollaborationStatus.ON_HOLD;
  }

  canResume(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) return false;
    return collaboration.status === CollaborationStatus.ON_HOLD;
  }

  canResend(collaboration: CollaborationItem): boolean {
    if (this.isSponsorOrg) return false;
    return collaboration.status === CollaborationStatus.PENDING;
  }

  canAcceptOrDecline(collaboration: CollaborationItem): boolean {
    return this.isSponsorOrg && collaboration.status === CollaborationStatus.PENDING;
  }

  hasNoActions(collaboration: CollaborationItem): boolean {
    return collaboration.status === CollaborationStatus.CLOSED;
  }

  canViewDataset(collaboration: CollaborationItem): boolean {
    if (!this.isSponsorOrg) return true;
    return collaboration.status === CollaborationStatus.ACTIVE;
  }

  trackByHeader(index: number, item: any): any {
    return item.name;
  }

  trackByCollaboration(index: number, item: CollaborationItem): string {
    return item.collaborationId as string;
  }

  onManageDatasets(collaboration: CollaborationItem) {
    this.collaborationDataService.clearData();
    this.collaborationDataService.updateCollaborationDetails(collaboration);
    if (this.isSponsorOrg) this.collaborationDataService.selectedDatasets = collaboration.partnerDatasets || [];
    else this.collaborationDataService.selectedDatasets = collaboration.ownerDatasets || [];
    this.router.navigate(['../dataset'], { relativeTo: this.activatedRoute });
  }

  declineInvite(data: CollaborationItem, declineModalTemplateRef: TemplateRef<void>): void {
    this.declineModalRef = this.nzModalService.create({
      nzTitle: 'Decline Invite',
      nzContent: declineModalTemplateRef,
      nzOnOk: () => this.handleDecline(data),
      nzFooter: null,
    });
  }

  closeCollaboration(data: CollaborationItem, closeModalTemplateRef: TemplateRef<void>): void {
    this.closeModalRef = this.nzModalService.create({
      nzTitle: 'Close Collaboration',
      nzContent: closeModalTemplateRef,
      nzOnOk: () => this.handleAction(data, Action.CLOSE, CollaborationStatus.CLOSED, 'close'),
      nzFooter: null,
    });
  }

  pauseCollaboration(data: CollaborationItem, pauseModalTemplateRef: TemplateRef<void>): void {
    this.pauseModalRef = this.nzModalService.create({
      nzTitle: 'Pause Collaboration',
      nzContent: pauseModalTemplateRef,
      nzOnOk: () => this.handleAction(data, Action.ON_HOLD, CollaborationStatus.ON_HOLD, 'pause'),
      nzFooter: null,
    });
  }

  handleAction(data: CollaborationItem, action: Action, status: CollaborationStatus, messageAction: string) {
    const { collaborationId, orgType, partnerOrgType } = data;
    if (!partnerOrgType) {
      this.nzMessageService.error('Partner organization not found, malformed data.');
      return;
    }
    this.isLoading = true;
    this.apiService
      .query<UpdateCollaborationMutation, UpdateCollaborationMutationVariables>(UpdateCollaboration, {
        collaborationRequest: {
          action: action,
          collaborationId: collaborationId!,
          orgType: orgType,
          partnerOrgType: partnerOrgType,
          status: status,
          lastModifiedBy: this.userManagementService.getEmail(),
        },
      })
      .then((response) => {
        const result = response?.data?.UpdateCollaboration;
        if (result?.success) {
          this.nzMessageService.success(result?.message || `Collaboration ${messageAction}d successfully`);
          this.appService.refreshEngagementList();
          this.loadCollaborationData();
        } else {
          this.nzMessageService.error(result?.message || `Collaboration ${messageAction} failed`);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message || `Collaboration ${messageAction} failed`);
      })
      .finally(() => (this.isLoading = false));
  }

  resendInvite(data: CollaborationItem) {
    const { collaborationId, orgType } = data;
    this.isLoading = true;
    this.apiService
      .query<SendCollaborationInviteMutation, SendCollaborationInviteMutationVariables>(SendCollaborationInvite, {
        collaborationRequest: {
          action: Action.SEND_INVITE,
          collaborationId: collaborationId!,
          orgType: orgType,
          lastModifiedBy: this.userManagementService.getEmail(),
        },
      })
      .then((response) => {
        const result = response.data?.SendCollaborationInvite;
        if (result?.success) {
          this.nzMessageService.success(`Invite resent successfully`);
          this.loadCollaborationData();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message || `Resend invite failed`);
      })
      .finally(() => (this.isLoading = false));
  }

  handleDecline(data: CollaborationItem) {
    const { collaborationId, orgType } = data;
    this.isLoading = true;
    this.apiService
      .query<AcceptOrDeclineCollaborationMutation, AcceptOrDeclineCollaborationMutationVariables>(
        AcceptOrDeclineCollaboration,
        {
          collaborationRequest: {
            action: Action.UPDATE,
            collaborationId: collaborationId!,
            orgType: orgType,
            status: CollaborationStatus.INVITATION_DECLINED,
            lastModifiedBy: this.userManagementService.getEmail(),
          },
        },
      )
      .then((response) => {
        const result = response?.data?.AcceptOrDeclineCollaboration;
        this.nzMessageService.success(result?.message || 'Invitation declined successfully');
        this.loadCollaborationData();
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message || 'Invitation declined failed');
      })
      .finally(() => (this.isLoading = false));
  }

  deleteCollaboration(collaboration: CollaborationItem, deleteModalTemplateRef: TemplateRef<void>): void {
    this.selectedCollaboration = collaboration;
    this.deleteModalRef = this.nzModalService.create({
      nzTitle: 'Delete Collaboration',
      nzContent: deleteModalTemplateRef,
      nzOnOk: () => this.handleDelete(),
      nzFooter: null,
    });
  }

  handleDelete() {
    if (!this.selectedCollaboration) return;

    const { collaborationId, orgType } = this.selectedCollaboration;
    this.isLoading = true;
    this.apiService
      .query<DeleteCollaborationMutation, DeleteCollaborationMutationVariables>(DeleteCollaboration, {
        collaborationRequest: {
          action: Action.DELETE,
          collaborationId: collaborationId!,
          orgType: orgType,
        },
      })
      .then((response) => {
        const result = response?.data?.DeleteCollaboration;
        this.nzMessageService.success(result?.message || 'Collaboration deleted successfully');
        this.selectedCollaboration = null;
        this.loadCollaborationData();
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message || 'Collaboration deletion failed');
      })
      .finally(() => (this.isLoading = false));
  }

  onViewDatasets(collaboration: CollaborationItem) {
    if (!this.canViewDataset(collaboration)) return;
    this.selectedCollaboration = collaboration;
    this.isViewDatasetsModalVisible = true;
  }

  getCollaboration(collaborationData: CollaborationItem) {
    this.isLoading = true;
    this.apiService
      .query<GetCollaborationQuery, GetCollaborationQueryVariables>(GetCollaboration, {
        collaborationRequest: {
          action: Action.GET_COLLABORATION,
          collaborationId: collaborationData.collaborationId!,
          orgType: this.userManagementService.getOrgType(),
          loggedInUserOrg: this.userManagementService.getOrganizationType() as OrgType,
        },
      })
      .then((result) => {
        this.isLoading = false;
        const existingCollaborationDetails = result?.data?.GetCollaboration;
        const { data } = existingCollaborationDetails || {};
        if (data) {
          if (data.status === CollaborationStatus.ACTIVE) {
            this.nzMessageService.info('Collaboration is already active, refreshing the data!');
            this.appService.refreshEngagementList();
            this.loadCollaborationData();
          } else {
            this.resendInvite(collaborationData);
          }
        }
      });
  }
}
